import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Loader2, AlertTriangle, CheckCircle2, Lock, Eye, EyeOff } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";

export default function AttorneyAccept() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const t = useT();
  const [phase, setPhase] = useState("loading"); // loading | form | submitting | error
  const [profile, setProfile] = useState(null);
  const [err, setErr] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setPhase("error");
      setErr(t("att.accept.invalid"));
      return;
    }
    api
      .get("/attorney/accept-invite", { params: { token } })
      .then((r) => {
        setProfile(r.data);
        setPhase("form");
      })
      .catch((e) => {
        setErr(formatApiError(e.response?.data?.detail) || t("att.accept.invalid"));
        setPhase("error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setErr(t("att.accept.weak_pwd"));
      return;
    }
    if (password !== confirm) {
      setErr(t("att.accept.mismatch"));
      return;
    }
    setErr("");
    setPhase("submitting");
    const token = params.get("token");
    try {
      await api.post("/attorney/accept-invite", { token, password });
      navigate("/attorney", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || t("att.accept.fail"));
      setPhase("form");
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2" data-testid="attorney-accept-page">
      <div className="hidden lg:flex flex-col justify-between p-12 text-white" style={{ background: "var(--brand)" }}>
        <Link to="/" className="text-sm font-bold text-white/70 hover:text-white">← Apex MVA</Link>
        <div>
          <div className="font-display text-4xl font-black tracking-tight">{t("att.portal.title")}</div>
          <p className="mt-3 max-w-sm text-white/70">{t("att.accept.intro")}</p>
        </div>
        <div className="text-xs text-white/40">{t("att.accept.disclaimer")}</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="card-soft w-full max-w-md p-8">
          <div className="grid h-10 w-10 place-items-center rounded-lg" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
            <Lock className="h-4 w-4 text-[#11322A]" />
          </div>

          {phase === "loading" && (
            <div className="mt-8 grid place-items-center" data-testid="att-accept-loading">
              <Loader2 className="h-7 w-7 animate-spin text-[#11322A]" />
              <p className="mt-3 text-sm text-[#5C6B68]">{t("att.accept.loading")}</p>
            </div>
          )}

          {phase === "error" && (
            <div className="mt-6 text-center" data-testid="att-accept-error">
              <AlertTriangle className="mx-auto h-10 w-10 text-[#B91C1C]" />
              <h2 className="mt-4 font-display text-xl font-black text-[#11322A]">{t("att.accept.error_title")}</h2>
              <p className="mt-2 text-sm text-[#5C6B68]">{err}</p>
              <Link to="/login" className="btn-secondary mt-6 inline-flex">{t("att.accept.go_login")}</Link>
            </div>
          )}

          {(phase === "form" || phase === "submitting") && profile && (
            <form onSubmit={submit} className="mt-6" data-testid="att-accept-form">
              <h1 className="font-display text-2xl font-black tracking-tight text-[#11322A]">
                {t("att.accept.welcome", { name: profile.name?.split(" ")[0] || "Counselor" })}
              </h1>
              <p className="mt-1 text-sm text-[#5C6B68]">
                {t("att.accept.subtitle", { firm: profile.firm_name || "Apex MVA" })}
              </p>

              <div className="mt-6 rounded-xl border border-[#E0E4E2] bg-[#FDFBF7] p-4 text-sm">
                <div className="label-cap text-[10px]">{t("att.accept.email_label")}</div>
                <div className="mt-1 font-bold text-[#11322A]">{profile.email}</div>
              </div>

              <div className="mt-5 space-y-4">
                <div>
                  <label className="label-cap">{t("att.accept.password")}</label>
                  <div className="relative mt-2">
                    <input
                      type={showPwd ? "text" : "password"}
                      className="input-field pr-10"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                      minLength={8}
                      data-testid="att-accept-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6B68] hover:text-[#11322A]"
                      data-testid="att-accept-toggle-pwd"
                      aria-label="Toggle password visibility"
                    >
                      {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="mt-1 text-[11px] text-[#5C6B68]">{t("att.accept.password_hint")}</p>
                </div>
                <div>
                  <label className="label-cap">{t("att.accept.confirm")}</label>
                  <input
                    type={showPwd ? "text" : "password"}
                    className="input-field mt-2"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    required
                    minLength={8}
                    data-testid="att-accept-confirm"
                  />
                </div>
                {err && (
                  <div className="rounded-lg border border-[#F5B6B0] bg-[#FDE8E6] px-3 py-2 text-sm text-[#B91C1C]" data-testid="att-accept-err">
                    {err}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={phase === "submitting"}
                  className="btn-primary w-full disabled:opacity-50"
                  data-testid="att-accept-submit"
                >
                  {phase === "submitting" ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> {t("att.accept.submitting")}
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4" /> {t("att.accept.cta")}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
